import { IAbstractRecord } from '@models';
import { FileType } from '@models/file';

import { GlobalLovInjector } from 'src/core/redux/lovs/types';

export enum FormFieldType {
  text = 'text',
  textArea = 'textArea',
  splitPrefix = 'newSplitPrefix',
  currency = 'currency',
  number = 'number',
  percentage = 'percentage',
  select = 'select',
  multiSelect = 'multiSelect',
  date = 'date',
  dateRange = 'dateRange',
  datetime = 'datetime',
  autocomplete = 'autocomplete',
  multiAutocomplete = 'multiAutocomplete',
  repeaterBox = 'repeaterBox',
  fileUploader = 'fileUploader',
  switch = 'switch',
  phoneNumber = 'phoneNumber',
  password = 'password',
  checkbox = 'checkbox',
  phonenbtext = 'phonenbtext'
}

export type DynamicFormWidgetInputType =
  | ITextFormFieldProps
  | ITextAreaFormFieldProps
  | ISplitPrefixFormFieldProps
  | ICurrencyFormFieldProps
  | INumberFormFieldProps
  | IPercentageFormFieldProps
  | ISelectFormFieldProps
  | IMultiSelectFormFieldProps
  | IDateFormFieldProps
  | IDateTimeFormFieldProps
  | IDateRangeFormFieldProps
  | IAutocompleteFormFieldProps
  | IMultiAutocompleteFormFieldProps
  | IRepeaterBoxFormFieldProps
  | IFileUploaderFormFieldProps
  | ISwitchFormFieldProps
  | IPhoneNumberFormFieldProps
  | IPasswordFormFieldProps
  | ICheckboxFormFieldProps
  | IPhonenbtextFormFieldProps;

export interface IFormFieldCommonProps {
  name: string;
  title: string;
  type: FormFieldType;
  placeholder?: string;
  value?: any;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  material?: boolean;
  hidden?: boolean;
  dynamicTitle?: string[]; // dynamic title for each repeater box
  customValidator?: (values: IAbstractRecord) => string; // return error message
  conditionalDisable?: (values: IAbstractRecord) => boolean; // return isDisabled or not
  conditionalHidden?: (values: IAbstractRecord) => boolean; // return isHidden or not
  conditionalRequired?: (values: IAbstractRecord) => boolean; // return isRequired or not
}

export interface ITextFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.text;
  minCharacters?: number;
  maxCharacters?: number;
}

export interface ITextAreaFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.textArea;
  numRows?: number;
  minCharacters?: number;
  maxCharacters?: number;
}

export interface ISplitPrefixFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.splitPrefix;
  inputType?: 'text' | 'number';
  prefixValue: string;
}

export interface ICurrencyFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.currency;
  currencySymbol?: string;
  allowNegative?: boolean;
  maxDecimalPrecision?: number;
}

export interface INumberFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.number;
  minValue?: number;
  maxValue?: number;
  allowNegativeValues?: boolean;
  allowExponential?: boolean;
  formatOnBlur?: boolean;
  maxDecimalPrecision?: number;
}

export interface IPercentageFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.percentage;
  minValue?: number;
  maxValue?: number;
  disableNegativeValues?: boolean;
}

// --- Select Field Types ---
interface ISelectFormFieldBase extends IFormFieldCommonProps {
  type: FormFieldType.select;
  canClearValue?: boolean;
  freeSolo?: boolean;
  triggerUpdate?: boolean;
}

// Branch 1: Local options only (no injectGlobalLov)
interface ISelectFormFieldWithLocalOptions extends ISelectFormFieldBase {
  selectOptions: Record<string, string>;
  injectGlobalLov?: never;
}

// Branch 2: Global LOV injection (no selectOptions)
interface ISelectFormFieldWithGlobalLov extends ISelectFormFieldBase {
  injectGlobalLov: GlobalLovInjector;
  selectOptions?: never;
}
export interface IPhonenbtextFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.phonenbtext;
}

// Final union for the “Select” form field
export type ISelectFormFieldProps =
  | ISelectFormFieldWithLocalOptions
  | ISelectFormFieldWithGlobalLov;

interface IMultiSelectFormFieldBase extends Omit<
  IFormFieldCommonProps,
  'value'
> {
  type: FormFieldType.multiSelect;
  value?: string[];
  freeSolo?: boolean;
  maxTagCount?: number;
  triggerUpdate?: boolean;
  showSelectAll?: boolean;
}

// Branch 1: Local options only (no injectGlobalLov)
interface IMultiSelectFormFieldWithLocalOptions extends IMultiSelectFormFieldBase {
  selectOptions: Record<string, string>;
  injectGlobalLov?: never;
}

// Branch 2: Global LOV injection (no selectOptions)
interface IMultiSelectFormFieldWithGlobalLov extends IMultiSelectFormFieldBase {
  injectGlobalLov: GlobalLovInjector;
  selectOptions?: never;
}

// Final union for the “MultiSelect” form field
export type IMultiSelectFormFieldProps =
  | IMultiSelectFormFieldWithLocalOptions
  | IMultiSelectFormFieldWithGlobalLov;

// --- Select Field Types END ---
export interface IDateFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.date;
  maxDate?: Date;
  minDate?: Date;
  canClearDate?: boolean;
  triggerUpdate?: boolean;
}

export interface IDateTimeFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.datetime;
  maxDate?: Date;
  minDate?: Date;
  canClearDate?: boolean;
  triggerUpdate?: boolean;
}

export interface IDateRangeFormFieldProps extends Omit<
  IFormFieldCommonProps,
  'value'
> {
  type: FormFieldType.dateRange;
  value: string[]; // keep it required since the component does not handle undefined
  maxDate?: Date;
  minDate?: Date;
  canClearDate?: boolean;
  triggerUpdate?: boolean;
}

export interface IAutocompleteFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.autocomplete;
  maxItemsDisplayed?: number;
  initialOptions?: Record<string, string>;
  canClearValue?: boolean;
  excludeIds?: string[] /** Exclude some option IDs when fetching LOV */;
}

export interface IMultiAutocompleteFormFieldProps extends Omit<
  IFormFieldCommonProps,
  'value'
> {
  type: FormFieldType.multiAutocomplete;
  value: string[]; // keep it required since the component does not handle undefined
  maxItemsDisplayed?: number;
  canClearValue?: boolean;
}

export interface IRepeaterBoxFormFieldProps extends Omit<
  IFormFieldCommonProps,
  'value'
> {
  type: FormFieldType.repeaterBox;
  value?: IAbstractRecord[];
  inputs: Record<string, DynamicFormWidgetInputType>;
  maxRepeaterLength?: number;
  minRepeaterLength?: number;
  addButtonText?: string;
  dynamicAddButtonText?: string[];
  filterDuplicatesByKey?: string;
}

export interface IFileUploaderFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.fileUploader;
  allowedFileTypes?: FileType[];
  maxFileSizeInMB?: number;
  downloadLinks?: {
    displayName: string;
    downloadUrl: string;
    action?: () => void | Promise<void>;
  }[];
  onUpload?: (file: File) => void;
  downloadButton?: {
    downloadUrl: string;
    action?: () => void;
  };
}

export interface ISwitchFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.switch;
  tooltipText?: string;
}

export interface IPhoneNumberFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.phoneNumber;
  countriesToShow?: string[];
  disableDropDown?: boolean;
  defaultCountry?: string;
  customFormat?: Record<string, string>;
  editCountryCode?: boolean;
}

export interface IPasswordFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.password;
  strongValidation?: boolean;
  hideHint?: boolean;
}

export interface ICheckboxFormFieldProps extends IFormFieldCommonProps {
  type: FormFieldType.checkbox;
}
